import { xboxOnboardSiteLocationPrefix } from "../constants";
import {
  SupportCase,
  SupportCaseReplyTarget,
  SupportCaseSeverity,
  SupportCaseStatus,
} from "../models/supportCases";

export const supportPagePath = `${xboxOnboardSiteLocationPrefix}/support`;
export const supportNewCasePath = `${supportPagePath}/new`;
const supportApiPath = `${supportPagePath}/api`;
export const getSupportCasePath = (caseId: string) =>
  `${supportPagePath}/${encodeURIComponent(caseId)}`;
export const getSupportAttachmentUrl = (caseId: string, attachmentId: string) =>
  `${supportApiPath}/cases/${encodeURIComponent(caseId)}/attachments/${encodeURIComponent(
    attachmentId
  )}`;

export enum SupportCardState {
  Submitted = "submitted",
  NewReply = "newReply",
  LastUpdated = "lastUpdated",
  AwaitingPartner = "awaitingPartner",
  Closed = "closed",
}

/** Support replies older than this are treated as waiting on the partner. */
const awaitingPartnerAfterMs = 3 * 24 * 60 * 60 * 1000;
const viewedStorageKey = "hubSupportCaseViewedOn";

export class SupportRequestError extends Error {
  public constructor(public readonly status: number) {
    super(`Support request failed with status ${status}`);
  }
}

export const fetchSupportJson = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(`${supportApiPath}${path}`, {
    credentials: "same-origin",
    ...init,
    headers: init?.body ? { "Content-Type": "application/json", ...init.headers } : init?.headers,
  });
  if (!response.ok) {
    throw new SupportRequestError(response.status);
  }
  return (await response.json()) as T;
};

/** Uploads one evidence file; attaches it to the reply when messageId is given. */
export const uploadSupportAttachment = async (caseId: string, file: File, messageId?: string) => {
  const query = new URLSearchParams({ fileName: file.name });
  if (messageId) {
    query.set("messageId", messageId);
  }
  const response = await fetch(
    `${supportApiPath}/cases/${encodeURIComponent(caseId)}/attachments?${query.toString()}`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    }
  );
  if (!response.ok) {
    throw new SupportRequestError(response.status);
  }
};

const readViewedOn = (): Record<string, string> => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(viewedStorageKey) ?? "{}");
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
};

export const markSupportCaseViewed = (caseId: string) => {
  try {
    localStorage.setItem(
      viewedStorageKey,
      JSON.stringify({ ...readViewedOn(), [caseId]: new Date().toISOString() })
    );
  } catch {
    // Storage can be unavailable (private mode); unread state is best effort.
  }
};

export const isSupportCaseClosed = ({ status }: SupportCase) =>
  status === SupportCaseStatus.Closed || status === SupportCaseStatus.Resolved;

/** Localized string key for a severity label; "<key>Description" holds its description. */
export const getSupportSeverityStringKey = (severity: SupportCaseSeverity) =>
  `hubSupportSeverity${severity.charAt(0).toUpperCase()}${severity.slice(1)}`;

const time = (value?: string) => (value ? Date.parse(value) || 0 : 0);

export const getSupportCardState = (
  supportCase: SupportCase,
  // CRM has no read receipts, so "seen" is tracked per browser.
  viewedOn = typeof window === "undefined" ? undefined : readViewedOn()[supportCase.caseId],
  now = Date.now()
): SupportCardState => {
  if (isSupportCaseClosed(supportCase)) {
    return SupportCardState.Closed;
  }
  const supportReply = time(supportCase.lastSupportReplyOn);
  if (!supportReply) {
    return SupportCardState.Submitted;
  }
  if (supportReply <= time(supportCase.lastPartnerReplyOn)) {
    return SupportCardState.LastUpdated;
  }
  if (supportReply > time(viewedOn)) {
    return SupportCardState.NewReply;
  }
  return supportCase.status === SupportCaseStatus.Pending ||
    now - supportReply > awaitingPartnerAfterMs
    ? SupportCardState.AwaitingPartner
    : SupportCardState.LastUpdated;
};

export const formatSupportDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const relativeUnits: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

export const formatRelativeTime = (value: string, now = Date.now()) => {
  const seconds = Math.round((time(value) - now) / 1000);
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const [unit, size] = relativeUnits.find(
    ([, unitSeconds]) => Math.abs(seconds) >= unitSeconds
  ) ?? ["second", 1];
  return format.format(Math.round(seconds / size), unit);
};

export enum SupportActivityType {
  Submitted = "submitted",
  Replied = "replied",
  Attention = "attention",
  Resolved = "resolved",
  Closed = "closed",
}

type SupportActivity = Readonly<{
  type: SupportActivityType;
  supportCase: SupportCase;
  on: string;
}>;

export const getRecentSupportActivity = (cases: ReadonlyArray<SupportCase>): SupportActivity[] =>
  cases
    .flatMap((supportCase): SupportActivity[] => {
      const activity: SupportActivity[] = [
        { type: SupportActivityType.Submitted, supportCase, on: supportCase.createdOn },
      ];
      if (supportCase.lastSupportReplyOn) {
        activity.push({
          type:
            getSupportCardState(supportCase) === SupportCardState.AwaitingPartner
              ? SupportActivityType.Attention
              : SupportActivityType.Replied,
          supportCase,
          on: supportCase.lastSupportReplyOn,
        });
      }
      if (isSupportCaseClosed(supportCase)) {
        activity.push({
          type:
            supportCase.status === SupportCaseStatus.Resolved
              ? SupportActivityType.Resolved
              : SupportActivityType.Closed,
          supportCase,
          on: supportCase.modifiedOn,
        });
      }
      return activity;
    })
    .sort((a, b) => time(b.on) - time(a.on))
    .slice(0, 5);

export const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

export const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join("");

export const toSupportReplyMailtoHref = (
  { address, subject, cc }: SupportCaseReplyTarget,
  body?: string
) =>
  `mailto:${encodeURIComponent(address)}?${
    cc?.length ? `cc=${cc.map(encodeURIComponent).join(",")}&` : ""
  }subject=${encodeURIComponent(subject)}${body ? `&body=${encodeURIComponent(body)}` : ""}`;
